/**
 * Single source of truth for plan *descriptors* — names, taglines, feature
 * lists, trial, ordering — on the pricing page, landing teaser, upgrade
 * sheet, and the trip-cap paywall. Mirrors docs/TIERS.md + ADR-0062.
 *
 * The dollar amounts are NOT hand-edited here: every build regenerates
 * ./pricing.generated.ts from Stripe and bakes it in, so the page can't
 * advertise a price Stripe doesn't charge (ADR-0089). To change a price,
 * edit it in Stripe; the next build picks it up.
 *
 * Display-only — server-side checkout never trusts these numbers; it
 * resolves the STRIPE_LOOKUP_* keys to the live price at request time.
 */

import { GENERATED_PRICES } from './pricing.generated';
import {
  ANNUAL_DISCOUNT_PERCENT,
  DEFAULT_TRIAL_DAYS,
  PAID_PLAN_IDS,
  type PaidPlanId,
} from './pricing-constants';

// Re-export so existing call sites keep importing these from pricing-plans.
// The values live in pricing-constants.ts (no deps) so build tooling can read
// them without loading this module's generated-price import (ADR-0089).
export { ANNUAL_DISCOUNT_PERCENT, DEFAULT_TRIAL_DAYS, PAID_PLAN_IDS };
export type { PaidPlanId };

export type PlanId = 'free' | 'pro' | 'pro-family' | 'business';
export type BillingPeriod = 'monthly' | 'annual';

export interface PlanPrice {
  monthly: number;
  /** Annual list price — the live Stripe annual amount (≈20% off monthly × 12). */
  annual: number;
  /** Effective monthly equivalent of the annual price — used for
   *  "from $X/mo billed annually" microcopy. */
  effectiveAnnualMonthly: number;
}

export interface PlanFeature {
  label: string;
  /** Whether this plan includes the capability — drives the check/✕ icon on
   *  /pricing and whether the landing teaser lists it at all. */
  included: boolean;
  /** Marks one of the plan's headline bullets. The landing teaser shows
   *  exactly the featured features collapsed and reveals the rest behind a
   *  "Show all features" toggle; /pricing always shows the full list.
   *  Curated per plan rather than derived by array position, so reordering a
   *  feature list can't silently change what the teaser advertises. */
  featured?: boolean;
}

export interface PlanDescriptor {
  id: PlanId;
  /** Display name (Tamagui Text). */
  name: string;
  /** One-line value prop shown under the name. */
  tagline: string;
  /** Number of people the plan covers. */
  seats: number;
  /** Price, or null for non-billed plans (Free, Business waitlist). */
  price: PlanPrice | null;
  /** Stripe checkout supports a trial for first-time paid plans.
   *  Mirrors DEFAULT_TRIAL_DAYS on the API side (ADR-0062). */
  trialDays: number | null;
  /** Bullet list of features. Each entry includes whether it's included
   *  on this plan — drives the comparison table. */
  features: ReadonlyArray<PlanFeature>;
  /** Whether to render this card as the highlighted "recommended" plan. */
  highlighted: boolean;
  /** CTA label override. Defaults to "Get started" / "Start free trial"
   *  based on price. */
  ctaLabel?: string | undefined;
  /** True when the plan is not yet shippable — render as a waitlist
   *  CTA instead of a checkout button. */
  waitlist?: boolean | undefined;
}

/** Assemble a PlanPrice from the build-time Stripe snapshot
 *  (./pricing.generated.ts). Monthly + annual are the real Stripe amounts;
 *  the effective monthly equivalent is derived from the actual annual price
 *  rather than computed from a discount formula (ADR-0089). */
function priceFor(id: PaidPlanId): PlanPrice {
  const { monthly, annual } = GENERATED_PRICES[id];
  return { monthly, annual, effectiveAnnualMonthly: Math.round((annual / 12) * 100) / 100 };
}

const WEATHER_FEATURE_LABEL = 'Weather forecasts — per day & place';

const PRO_FEATURES: ReadonlyArray<PlanFeature> = [
  // The first four are Pro's teaser headline (`featured`): the capabilities a
  // free user doesn't have and the most common reasons to upgrade.
  { label: 'Unlimited private trips', included: true, featured: true },
  { label: 'Trip sharing & collaboration', included: true, featured: true },
  { label: 'Live-traffic routing + transit schedules', included: true, featured: true },
  { label: WEATHER_FEATURE_LABEL, included: true, featured: true },
  { label: 'Place opening hours on cards', included: true },
  { label: 'Ratings & smart ranked place suggestions', included: true },
  { label: 'Multi-currency cost reporting', included: true },
  { label: 'Calendar subscription (iCal)', included: true },
  { label: 'Offline mode with auto-sync', included: true },
  { label: 'More AI — higher limits + vault reads', included: true },
  { label: 'MCP server — connect your AI tools', included: true },
  { label: 'Priority email support', included: true },
];

// Family Pro inherits every Pro feature but re-curates its teaser top 4: the
// seat count is the whole reason to pick this tier over Pro, so it takes a
// featured slot. To keep exactly four headline bullets, Weather steps out of
// the featured set (it's still listed in the expanded view and on /pricing).
const PRO_FAMILY_FEATURES: ReadonlyArray<PlanFeature> = [
  ...PRO_FEATURES.map((feature) =>
    feature.label === WEATHER_FEATURE_LABEL ? { ...feature, featured: false } : feature
  ),
  { label: 'Up to 5 seats — one person pays', included: true, featured: true },
  { label: 'Invite by email, manage seats from /family', included: true },
];

export const PLANS: ReadonlyArray<PlanDescriptor> = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Plan a trip, end-to-end.',
    seats: 1,
    price: null,
    trialDays: null,
    highlighted: false,
    features: [
      // Teaser top 4 (`featured`): the trip cap sets expectations, the other
      // three show the Free planner is genuinely capable, not a crippled demo.
      { label: 'Up to 3 private trips', included: true, featured: true },
      { label: 'Unlimited shared trips received (read-only)', included: true },
      { label: 'Unlimited editing with one-tap undo', included: true },
      { label: 'Place search with cached photos', included: true },
      { label: 'Static distance-based route estimates', included: true },
      { label: 'Flights & hotels as first-class items', included: true, featured: true },
      { label: 'Smart trip references — visa, advisories, currency', included: true },
      { label: 'Document vault for bookings & passports', included: true },
      { label: 'Cost tracking & splitting', included: true, featured: true },
      { label: 'AI assistant, itinerary drafts & booking parser', included: true, featured: true },
      { label: 'JSON export / import', included: true },
      { label: 'Trip sharing & collaboration (Pro)', included: false },
      { label: 'Live-traffic routing (Pro)', included: false },
      { label: 'Weather + opening hours (Pro)', included: false },
      { label: 'Cost reporting (Pro)', included: false },
    ],
    ctaLabel: 'Get started',
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Take it everywhere, plan it well.',
    seats: 1,
    price: priceFor('pro'),
    trialDays: DEFAULT_TRIAL_DAYS,
    highlighted: true,
    features: PRO_FEATURES,
    ctaLabel: 'Start 7-day free trial',
  },
  {
    id: 'pro-family',
    name: 'Family Pro',
    tagline: 'Everything in Pro, for up to 5 people.',
    seats: 5,
    price: priceFor('pro-family'),
    trialDays: DEFAULT_TRIAL_DAYS,
    highlighted: false,
    features: PRO_FAMILY_FEATURES,
    ctaLabel: 'Start 7-day free trial',
  },
  {
    id: 'business',
    name: 'Business',
    tagline: 'Team accounts, SSO, admin controls.',
    seats: 0,
    price: null,
    trialDays: null,
    highlighted: false,
    waitlist: true,
    features: [
      { label: 'Everything in Pro', included: true },
      { label: 'Team accounts (coming soon)', included: false },
      { label: 'SSO + audit log (coming soon)', included: false },
      { label: 'Branded share pages (coming soon)', included: false },
    ],
    ctaLabel: 'Join waitlist',
  },
] as const;

/** Consumer plans only (Free, Pro, Family Pro) — the Business waitlist
 *  tier is excluded. This is the exact set the landing-page teaser and the
 *  upgrade sheet render; /pricing renders the full PLANS array. Derived
 *  here (rather than re-`filter`ed at each call site) so the "teaser is a
 *  subset of the catalog" contract has one home and one place to test. */
export const CONSUMER_PLANS: ReadonlyArray<PlanDescriptor> = PLANS.filter(
  (plan) => !plan.waitlist
);

export function getPlan(id: PlanId): PlanDescriptor {
  const p = PLANS.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown plan: ${id}`);
  return p;
}

/** The plan's headline bullets for the landing teaser's collapsed card — the
 *  curated `featured` features, in list order. Authored to be exactly four per
 *  consumer plan (see pricing-plans.test.ts). */
export function featuredFeatures(plan: PlanDescriptor): ReadonlyArray<PlanFeature> {
  return plan.features.filter((f) => f.included && f.featured);
}

/** The remaining included features, revealed when the teaser card's
 *  "Show all features" toggle is expanded. Excludes the featured bullets
 *  (already shown) and the not-included upsell rows (teaser is all-positive). */
export function additionalFeatures(plan: PlanDescriptor): ReadonlyArray<PlanFeature> {
  return plan.features.filter((f) => f.included && !f.featured);
}

/** Render a dollar amount with the currency symbol and two decimals:
 *  `formatDollars(6.41)` → "$6.41". The single place the `$` symbol and
 *  two-decimal convention live, so every price string (cards, microcopy,
 *  the landing teaser's headline number) reads identically. Prices are the
 *  USD base list price; under Stripe Adaptive Pricing the buyer's local
 *  currency is applied at checkout, not on the marketing page (ADR-0089). */
export function formatDollars(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Format a price for display: "$66.99/yr" or "$6.99/mo". Free / waitlist
 *  plans (price === null) format as "Free" so callsites don't have to
 *  guard on every render. */
export function formatPrice(price: PlanPrice | null, period: BillingPeriod): string {
  if (!price) return 'Free';
  if (period === 'annual') return `${formatDollars(price.annual)}/yr`;
  return `${formatDollars(price.monthly)}/mo`;
}

/** Headline label for a plan card with no live price (price === null).
 *  The Free tier reads "$0" — a price, so it doesn't just echo the plan's
 *  "Free" name and it lines up with the "$X.99" headlines on the paid cards.
 *  Waitlist tiers (Business) have no price set yet, so they read "TBD" rather
 *  than implying the plan costs nothing. */
export function unpricedPlanLabel(plan: PlanDescriptor): string {
  return plan.waitlist ? 'TBD' : '$0';
}

/** Effective monthly equivalent of an annual price: "$5.58/mo billed annually".
 *  Returns an empty string when price is null so the consumer can render
 *  no microcopy at all rather than "$NaN/mo billed annually". */
export function formatAnnualAsMonthly(price: PlanPrice | null): string {
  if (!price) return '';
  return `${formatDollars(price.effectiveAnnualMonthly)}/mo billed annually`;
}

/** Whole-dollar annual saving vs paying monthly × 12: "Save $19/yr". Used as
 *  a conversion nudge next to the annual price. Rounds to whole dollars (the
 *  cents are noise next to a ~$19–$43 saving) and returns an empty string for
 *  unpriced plans so the consumer renders no badge. */
export function formatAnnualSavings(price: PlanPrice | null): string {
  if (!price) return '';
  const savings = Math.round(price.monthly * 12 - price.annual);
  // A misconfigured annual ≥ monthly × 12 would otherwise read "Save $-5/yr";
  // show no nudge rather than a negative or zero saving.
  if (savings <= 0) return '';
  return `Save $${savings}/yr`;
}

/** Whole-percent annual discount vs paying monthly × 12, rounded to nearest:
 *  Pro ($76.99 vs $95.88 → 19.7%) → 20. Computed from the live prices so the
 *  "Save N%" badge always matches what's actually charged — no hard-coded
 *  number to drift from Stripe (ADR-0089). Returns 0 for unpriced plans
 *  (callers don't render a badge then). */
export function annualDiscountPercent(price: PlanPrice | null): number {
  if (!price) return 0;
  return Math.round((1 - price.annual / (price.monthly * 12)) * 100);
}
